import { IPillItem } from '~/src/dash/components/pill-switcher';
import { publicaColors } from '~/src/dash/utils/colors';
import { getStartDate } from '~/src/dash/utils/date';
import {
  IDateRanges,
  IReportBuilderConfig,
  IReportBuilderFilters,
  IReportTypes,
  ISections,
  ITableDimensions,
  ITableMetrics,
} from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/common/types';
import { CustomizeDataRow } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections/CustomizeData';
import { DimensionsRow } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections/Dimensions';
import { MetricsRow } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections/Metrics';
import { OverviewRow } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections/Overview';
import { QueryRow } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report/Sections/Query';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';

export const CUSTOMIZED_METRICS_CATEGORIES = {
  SUMMARY: 'Summary',
  AD_REFRESH: 'Ad Refresh',
  EXPOSURE: 'Exposure',
  GROUPM_VIEWABILITY: 'Group M Viewability',
  PUBLICIS_VIEWABILITY: 'Publicis Viewability',
  OUT_OF_VIEW_ADS: 'Out of View Ads',
  INVALID_TRAFFIC: 'Invalid Traffic',
  INVALID_TRAFFIC_BY_TYPE: 'Invalid Traffic By Type',
  FAILURE_REASONS: 'Failure Reasons',
  FAILURE_RISK_CATEGORIES: 'Failure Risk Categories',
  BLOCKED_REASONS: 'Blocked Reasons',
  BLOCKED_RISK_CATEGORIES: 'Blocked Risk Categories',
  VIDEO: 'Video',
};

export const reportBuilderConfig = {
  // these are static filters requiring no props
  // NOTE: MENUTITLE MUST MATCH OBJECT KEY
  filters: {
    Formats: {
      menuTitle: 'Formats',
      list: {
        '001': {
          ID: '001',
          title: 'Display',
          selected: false,
        },
        '002': {
          ID: '002',
          title: 'Video',
          selected: false,
        },
      },
      singularString: 'Format',
      paramKey: 'formats',
    },
    'Device Types': {
      menuTitle: 'Device Types',
      list: {
        101: {
          ID: '101',
          title: 'Desktop',
          selected: false,
        },
        102: {
          ID: '102',
          title: 'Mobile',
          selected: false,
        },
        103: {
          ID: '103',
          title: 'Tablet',
          selected: false,
        },
      },
      singularString: 'Device Type',
      paramKey: 'devicetypes',
    },
  } as IReportBuilderFilters,
  // NOTE: LABEL MUST MATCH OBJECT KEY
  // when adding new groupBys, make sure to add the keys to ITableGroupByKey
  dimensions: {
    Advertiser: {
      label: 'Advertiser',
      apiKey: 'advertiser',
      subheader: 'Delivery',
    },
    Order: {
      label: 'Order',
      apiKey: 'order',
      subheader: 'Delivery',
    },
    'Line Item': {
      label: 'Line Item',
      apiKey: 'lineItem',
      subheader: 'Delivery',
    },
    Creative: {
      label: 'Creative',
      apiKey: 'creative',
      subheader: 'Delivery',
    },
    'Ad Unit': {
      label: 'Ad Unit',
      apiKey: 'adUnit',
      subheader: 'Inventory',
    },
    Size: {
      label: 'Size',
      apiKey: 'adSize',
      subheader: 'Inventory',
    },
    'Traffic Source': {
      label: 'Traffic Source',
      apiKey: 'Custom1Name',
      subheader: 'Inventory',
    },
    Tile: {
      label: 'Tile',
      apiKey: 'Custom2Name',
      subheader: 'Inventory',
    },
    Section: {
      label: 'Section',
      apiKey: 'Custom3Name',
      subheader: 'Inventory',
    },
    Date: {
      label: 'Date',
      apiKey: 'dateCols',
      subheader: 'Other',
    },
    'Device Type': {
      label: 'Device Type',
      apiKey: 'deviceType',
      subheader: 'Other',
    },
    Format: {
      label: 'Format',
      apiKey: 'mediaFormat',
      subheader: 'Other',
    },
    Country: {
      label: 'Country',
      apiKey: 'country',
      subheader: 'Other',
    },
  } as ITableDimensions,
  mainMetrics: {
    'Total Tracked Ads': {
      label: 'Total Tracked Ads',
      apiKey: 'TotalImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Rendered Impressions': {
      label: 'Rendered Impressions',
      apiKey: 'RenderedImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Invalid Traffic Ads': {
      label: 'Invalid Traffic Ads',
      apiKey: 'IVTAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Measured Ads': {
      label: 'Measured Ads',
      apiKey: 'MeasuredAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Viewable Impressions': {
      label: 'Viewable Impressions',
      apiKey: 'ViewableImpression',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Blocked Ads': {
      label: 'Blocked Ads',
      apiKey: 'blockedImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Render Rate': {
      label: 'Render Rate',
      apiKey: 'RenderRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Invalid Traffic Rate': {
      label: 'Invalid Traffic Rate',
      apiKey: 'IVTRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Measured Rate': {
      label: 'Measured Rate',
      apiKey: 'MeasuredRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Viewability Rate': {
      label: 'Viewability Rate',
      apiKey: 'ViewabilityRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Blocked Rate': {
      label: 'Blocked Rate',
      apiKey: 'AdBlockRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'GroupM Display Impressions': {
      label: 'GroupM Display Impressions',
      apiKey: 'GroupmFullyInViewDisplayImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'GroupM Video Impressions': {
      label: 'GroupM Video Impressions',
      apiKey: 'GroupmFullyInViewVideoImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'Total Billable Impressions': {
      label: 'Total Billable Impressions',
      apiKey: 'TotalBillableImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'GroupM Display Viewable Rate': {
      label: 'GroupM Display Viewable Rate',
      apiKey: 'GroupmDisplayViewableRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'GroupM Video Viewable Rate': {
      label: 'GroupM Video Viewable Rate',
      apiKey: 'GroupmVideoViewableRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'Sitting Duck Bot Impressions': {
      label: 'Sitting Duck Bot Impressions',
      apiKey: 'SittingDuckBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Standard Bot Impressions': {
      label: 'Standard Bot Impressions',
      apiKey: 'StandardBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Volunteer Bot Impressions': {
      label: 'Volunteer Bot Impressions',
      apiKey: 'VolunteerBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Profile Bot Impressions': {
      label: 'Profile Bot Impressions',
      apiKey: 'ProfileBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Masked Bot Impressions': {
      label: 'Masked Bot Impressions',
      apiKey: 'MaskedBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Nomadic Bot Impressions': {
      label: 'Nomadic Bot Impressions',
      apiKey: 'NomadicBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Other Bot Impressions': {
      label: 'Other Bot Impressions',
      apiKey: 'OtherBotImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Failed Adult Ads': {
      label: 'Failed Adult Ads',
      apiKey: 'FailedAdultAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Alcohol Ads': {
      label: 'Failed Alcohol Ads',
      apiKey: 'FailedAlcoholAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Hate Speech Ads': {
      label: 'Failed Hate Speech Ads',
      apiKey: 'FailedHateSpeechAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Illegal Downloads Ads': {
      label: 'Failed Illegal Downloads Ads',
      apiKey: 'FailedIllegalDownloadsAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Drugs Ads': {
      label: 'Failed Drugs Ads',
      apiKey: 'FailedDrugsAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Offensive Language Ads': {
      label: 'Failed Offensive Language Ads',
      apiKey: 'FailedOffensiveLanguageAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Violence Ads': {
      label: 'Failed Violence Ads',
      apiKey: 'FailedViolenceAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Refresh Ads': {
      label: 'Refresh Ads',
      apiKey: 'RefreshImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'IAS Refreshed Ads': {
      label: 'IAS Refreshed Ads',
      apiKey: 'RefreshThresholdEvents',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'Refreshed Ad Rate': {
      label: 'Refreshed Ad Rate',
      apiKey: 'RefreshedAdRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'IAS Refreshed Ad Rate': {
      label: 'IAS Refreshed Ad Rate',
      apiKey: 'IASRefreshedAdRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'Publicis Fully In View Display Impressions': {
      label: 'Publicis Fully In View Display Impressions',
      apiKey: 'PublicisFullyInViewDisplayImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Publicis Fully In View Video Impressions': {
      label: 'Publicis Fully In View Video Impressions',
      apiKey: 'PublicisFullyInViewVideoImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Publicis Display Viewable Rate': {
      label: 'Publicis Display Viewable Rate',
      apiKey: 'PublicisDisplayViewableRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Publicis Video Viewable Rate': {
      label: 'Publicis Video Viewable Rate',
      apiKey: 'PublicisVideoViewableRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Blocked by Brand Safety': {
      label: 'Blocked by Brand Safety',
      apiKey: 'BlockedContentImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Brand Suitability': {
      label: 'Blocked by Brand Suitability',
      apiKey: 'BlockedSuitabilityImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Custom Keyword Exclusion List': {
      label: 'Blocked by Custom Keyword Exclusion List',
      apiKey: 'BlockedKeywordImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Custom URL/App Exclusion List': {
      label: 'Blocked by Custom URL/App Exclusion List',
      apiKey: 'BlockedCustomListImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Geo': {
      label: 'Blocked by Geo',
      apiKey: 'BlockedGeoImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Invalid Traffic': {
      label: 'Blocked by Invalid Traffic',
      apiKey: 'BlockedSuspiciousImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Invisible URL': {
      label: 'Blocked by Invisible URL',
      apiKey: 'BlockedSeeThroughImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Target Language Mismatch': {
      label: 'Blocked by Target Language Mismatch',
      apiKey: 'BlockedLanguageImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'In View At Page Open Rate': {
      label: 'In View At Page Open Rate',
      apiKey: 'InViewAtPageOpenRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.EXPOSURE,
    },
    'In View > 5s Rate': {
      label: 'In View > 5s Rate',
      apiKey: 'InViewOver5sRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.EXPOSURE,
    },
    'In View > 15s Rate': {
      label: 'In View > 15s Rate',
      apiKey: 'InViewOver15sRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.EXPOSURE,
    },
    'Not Enough Time': {
      label: 'Not Enough Time',
      apiKey: 'MrcOvrNetImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Not Enough Pixels': {
      label: 'Not Enough Pixels',
      apiKey: 'MrcOvrNepImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Never On Screen': {
      label: 'Never On Screen',
      apiKey: 'MrcOvrNivImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Out Of Focus': {
      label: 'Out Of Focus',
      apiKey: 'MrcOvrOofImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Video Never Started': {
      label: 'Video Never Started',
      apiKey: 'MrcOvrNsImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Other Out Of View': {
      label: 'Other Out Of View',
      apiKey: 'MrcOvrUnkImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Failed for Brand Safety': {
      label: 'Failed for Brand Safety',
      apiKey: 'FailedContentImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Brand Suitability': {
      label: 'Failed for Brand Suitability',
      apiKey: 'FailedSuitabilityImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Custom Keyword Exclusion List': {
      label: 'Failed for Custom Keyword Exclusion List',
      apiKey: 'FailedKeywordImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Custom URL/App Exclusion List': {
      label: 'Failed for Custom URL/App Exclusion List',
      apiKey: 'FailedCustomListImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Geo': {
      label: 'Failed for Geo',
      apiKey: 'FailedGeoImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Invalid Traffic': {
      label: 'Failed for Invalid Traffic',
      apiKey: 'FailedSuspiciousImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Invisible URL': {
      label: 'Failed for Invisible URL',
      apiKey: 'FailedSeeThroughImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Target Language Mismatch': {
      label: 'Failed for Target Language Mismatch',
      apiKey: 'FailedLanguageImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Blocked Adult Ads': {
      label: 'Blocked Adult Ads',
      apiKey: 'BlockedContentAdultImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Alcohol Ads': {
      label: 'Blocked Alcohol Ads',
      apiKey: 'BlockedContentAlcoholImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Hate Speech Ads': {
      label: 'Blocked Hate Speech Ads',
      apiKey: 'BlockedContentHateImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Illegal Downloads Ads': {
      label: 'Blocked Illegal Downloads Ads',
      apiKey: 'BlockedContentDownloadsImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Drugs Ads': {
      label: 'Blocked Drugs Ads',
      apiKey: 'BlockedContentDrugsImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Offensive Language Ads': {
      label: 'Blocked Offensive Language Ads',
      apiKey: 'BlockedContentOffensiveImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Violence Ads': {
      label: 'Blocked Violence Ads',
      apiKey: 'BlockedContentViolenceImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    '% In View': {
      label: '% In View',
      apiKey: 'VideoInViewRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Out Of View': {
      label: '% Out Of View',
      apiKey: 'VideoOutOfViewRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Ad Never Started': {
      label: '% Ad Never Started',
      apiKey: 'VideoNeverStartedRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Completed': {
      label: '% Completed',
      apiKey: 'VideoCompletedRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Completed In View': {
      label: '% Completed In View',
      apiKey: 'VideoCompletedInViewRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Muted': {
      label: '% Muted',
      apiKey: 'VideoMutedRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Full Screen': {
      label: '% Full Screen',
      apiKey: 'VideoFullScreenRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Click Through (CTR)': {
      label: '% Click Through (CTR)',
      apiKey: 'VideoClickThroughRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    'General Invalid Traffic (GIVT) Ads': {
      label: 'General Invalid Traffic (GIVT) Ads',
      apiKey: 'FraudGivtImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Bots (SIVT) Ads': {
      label: 'Bots (SIVT) Ads',
      apiKey: 'FraudNhtImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Hidden Ads (SIVT)': {
      label: 'Hidden Ads (SIVT)',
      apiKey: 'FraudHaImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Domain Spoofing (SIVT)': {
      label: 'Domain Spoofing (SIVT)',
      apiKey: 'FraudDsImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Incentivized Browsing (RVI) Ads': {
      label: 'Incentivized Browsing (RVI) Ads',
      apiKey: 'FraudIbImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Proxy Servers (RVI) Ads': {
      label: 'Proxy Servers (RVI) Ads',
      apiKey: 'FraudLspImps',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Sophisticated Invalid Traffic Ads': {
      label: 'Sophisticated Invalid Traffic Ads',
      apiKey: 'SophisticatedInvalidTrafficAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Reduced Value Inventory Ads': {
      label: 'Reduced Value Inventory Ads',
      apiKey: 'ReducedValueInventoryAds',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'General Invalid Traffic Rate': {
      label: 'General Invalid Traffic Rate',
      apiKey: 'GeneralInvalidTrafficRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Sophisticated Invalid Traffic Rate': {
      label: 'Sophisticated Invalid Traffic Rate',
      apiKey: 'SophisticatedInvalidTrafficRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Reduced Value Inventory Rate': {
      label: 'Reduced Value Inventory Rate',
      apiKey: 'ReducedValueInventoryRate',
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
  } as ITableMetrics,
  threshold: {
    'All Tracked Ads': 'all',
    'Min Tracked Ads: 250': 250,
    'Min Tracked Ads:   1k': 1000,
    'Min Tracked Ads:  10k': 10000,
    'Min Tracked Ads: 100k': 100000,
    'Min Tracked Ads:   1m': 1000000,
    Custom: 'Custom',
  },
  compareTypes: [
    { label: 'Greater Than', value: 'GT' },
    { label: 'Greater Than or equal', value: 'GTE' },
    { label: 'Less Than', value: 'LT' },
    { label: 'Less Than or equal', value: 'LTE' },
    { label: 'Between', value: 'BT' },
    { label: 'Equal to', value: 'ET' },
  ] as ISingleSelectOption[],
  reportFrequencies: [
    { label: 'Once', value: 'ONE_TIME' },
    { label: 'Daily', value: 'DAILY' },
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Monthly', value: 'MONTHLY' },
  ] as ISingleSelectOption[],
  deliveryTypes: [
    {
      text: 'Email',
      activeTextColor: publicaColors.jungleGreen,
      activeBackgroundColor: publicaColors.honeydew,
    },
    {
      text: 'AWS S3',
      activeTextColor: publicaColors.jungleGreen,
      activeBackgroundColor: publicaColors.honeydew,
    },
    {
      text: 'SFTP',
      activeTextColor: publicaColors.jungleGreen,
      activeBackgroundColor: publicaColors.honeydew,
    },
  ] as IPillItem[],
  reportTypes: {
    'Performance Report': {
      title: 'Performance Report',
      label: 'IAS measured data',
      apiKey: 'IAS_PERFORMANCE',
    },
    'Device Report': {
      title: 'Device Report',
      label: 'IAS data for device breakouts',
      apiKey: 'DEVICE_PERFORMANCE',
    },
    'Context Control Report': {
      title: 'Context Control Report',
      label: 'IAS data for reporting by segment',
      apiKey: 'CONTEXT_CONTROL_REPORT',
    },
  } as IReportTypes,
  dateRange: {
    LAST_7_DAYS: {
      title: 'Last 7 Days',
      startDate: getStartDate('America/New_York', 7, 'day').startOf('day'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'LAST_7_DAYS',
    },
    YESTERDAY: {
      title: 'Yesterday',
      startDate: getStartDate('America/New_York', 1, 'day').startOf('day'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'YESTERDAY',
    },
    TWO_DAYS_AGO: {
      title: 'Two Days Ago',
      startDate: getStartDate('America/New_York', 2, 'day').startOf('day'),
      endDate: getStartDate('America/New_York', 2, 'day').endOf('day'),
      apiKey: 'TWO_DAYS_AGO',
    },
    LAST_14_DAYS: {
      title: 'Last 14 Days',
      startDate: getStartDate('America/New_York', 14, 'day').startOf('day'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'LAST_14_DAYS',
    },
    LAST_30_DAYS: {
      title: 'Last 30 Days',
      startDate: getStartDate('America/New_York', 30, 'day').startOf('day'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'LAST_30_DAYS',
    },
    THIS_WEEK: {
      title: 'This Week',
      startDate: getStartDate('America/New_York', 1, 'week').startOf('week'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'THIS_WEEK',
    },
    LAST_WEEK: {
      title: 'Last Week',
      startDate: getStartDate('America/New_York', 1, 'week').startOf('week'),
      endDate: getStartDate('America/New_York', 1, 'week').endOf('week'),
      apiKey: 'LAST_WEEK',
    },
    THIS_MONTH: {
      title: 'This Month',
      startDate: getStartDate('America/New_York', 0, 'month').startOf('month'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'THIS_MONTH',
    },
    LAST_MONTH: {
      title: 'Last Month',
      startDate: getStartDate('America/New_York', 1, 'month').startOf('month'),
      endDate: getStartDate('America/New_York', 1, 'month').endOf('month'),
      apiKey: 'LAST_MONTH',
    },
    THIS_QUARTER: {
      title: 'This Quarter',
      startDate: getStartDate('America/New_York', 0, 'quarter').startOf('quarter'),
      endDate: getStartDate('America/New_York', 1, 'day').endOf('day'),
      apiKey: 'THIS_QUARTER',
    },
    LAST_QUARTER: {
      title: 'Last Quarter',
      startDate: getStartDate('America/New_York', 1, 'quarter').startOf('quarter'),
      endDate: getStartDate('America/New_York', 1, 'quarter').endOf('quarter'),
      apiKey: 'LAST_QUARTER',
    },
    // 'Campaign to Date': {
    //   title: 'Campaign to Date',
    //   startDate: getStartDate('America/New_York', 7, 'day').startOf('day'),
    //   endDate: getStartDate('America/New_York', 1, 'day').startOf('day'),
    //   apiKey: 'LAST_7_DAYS',
    // },
    CUSTOM: {
      title: 'Custom Range...',
      startDate: getStartDate('America/New_York', 7, 'day').startOf('day'),
      endDate: getStartDate('America/New_York', 1, 'day').startOf('day'),
      apiKey: 'CUSTOM',
    },
  } as IDateRanges,
  sections: {
    Overview: {
      title: 'Overview',
      icon: '/assets/shared/svg/publisherPlatform/Overview.svg',
      component: OverviewRow,
    },
    Query: {
      title: 'Query',
      icon: '/assets/shared/svg/publisherPlatform/Query.svg',
      component: QueryRow,
    },
    Dimensions: {
      title: 'Dimensions',
      icon: '/assets/shared/svg/publisherPlatform/Dimensions.svg',
      component: DimensionsRow,
    },
    Metrics: {
      title: 'Metrics',
      icon: '/assets/shared/svg/publisherPlatform/Metrics.svg',
      component: MetricsRow,
    },
    'Customize Data': {
      title: 'Customize Data',
      icon: '/assets/shared/svg/publisherPlatform/Customize.svg',
      component: CustomizeDataRow,
    },
  } as ISections,
} as IReportBuilderConfig;
